import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Comisiones.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Comisiones = () => {
    const [comisiones, setComisiones] = useState([]);
    const [trabajadores, setTrabajadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [filtroTrabajador, setFiltroTrabajador] = useState('todos');
    const [filtroPagoCliente, setFiltroPagoCliente] = useState('todos');
    const [busqueda, setBusqueda] = useState('');
    const [fechaInicio, setFechaInicio] = useState(null);
    const [fechaFin, setFechaFin] = useState(null);
    const [userInfo, setUserInfo] = useState(null);

    useEffect(() => {
        // Obtener información del usuario logueado
        const userData = localStorage.getItem('user');
        if (userData) {
            const user = JSON.parse(userData);
            setUserInfo(user);
            
            // Si es trabajador, filtrar automáticamente por sus comisiones
            if (user.rol === 'trabajador') {
                setFiltroTrabajador(user.id);
            }
        }
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Obtener todas las comisiones
            const response = await axios.get('https://backend-vbfh.onrender.com/api/comisiones');
            
            // Obtener lista de trabajadores
            const trabajadoresRes = await axios.get('https://backend-vbfh.onrender.com/api/usuarios?rol=trabajador');
            
            if (response.data.success && trabajadoresRes.data) {
                setComisiones(response.data.data || []);
                setTrabajadores(trabajadoresRes.data || []);
            } else {
                throw new Error('Error al cargar datos');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.response?.data?.error || error.message || 'Error al cargar comisiones');
        } finally {
            setLoading(false);
        }
    };

    const aplicarFiltros = () => {
        let filtered = [...comisiones];
        
        // Filtro de búsqueda
        if (busqueda) {
            filtered = filtered.filter(c => 
                c.trabajador_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
                c.cliente_pedido?.toLowerCase().includes(busqueda.toLowerCase()) ||
                c.numeroremision?.toString().includes(busqueda)
            );
        }
        
        // Filtro por estado
        if (filtroEstado !== 'todos') {
            filtered = filtered.filter(c => c.estado === filtroEstado);
        }
        
        // Filtro por trabajador
        if (filtroTrabajador !== 'todos') {
            filtered = filtered.filter(c => c.trabajador_id == filtroTrabajador);
        }
        
        // Filtro por pago cliente
        if (filtroPagoCliente !== 'todos') {
            filtered = filtered.filter(c => 
                filtroPagoCliente === 'Sí' ? c.cliente_pago === 'Sí' : c.cliente_pago !== 'Sí'
            );
        }
        
        // Filtro por fechas
        if (fechaInicio && fechaFin) {
            const inicio = new Date(fechaInicio);
            inicio.setHours(0, 0, 0, 0);
            
            const fin = new Date(fechaFin);
            fin.setHours(23, 59, 59, 999);
            
            filtered = filtered.filter(c => {
                const fechaComision = new Date(c.fecha);
                return fechaComision >= inicio && fechaComision <= fin;
            });
        }
        
        return filtered;
    };

    const comisionesFiltradas = aplicarFiltros();

    const updateEstado = async (id, estado) => {
        if (!window.confirm(`¿Estás seguro de cambiar el estado a ${estado}?`)) return;
        
        try {
            const response = await axios.put(
                `https://backend-vbfh.onrender.com/api/comisiones/${id}/estado`, 
                { estado }
            );
            
            if (response.data.success) {
                alert(response.data.message);
                fetchData();
            } else {
                throw new Error(response.data.error);
            }
        } catch (error) {
            console.error('Error updating comision:', error);
            alert(`Error: ${error.response?.data?.error || error.message}`);
        }
    };

    const calcularTotalComision = (monto, porcentaje) => {
        return (monto * porcentaje / 100).toFixed(2);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatFecha = (fecha) => {
        if (!fecha) return 'N/A';
        try {
            const fechaObj = new Date(fecha);
            return fechaObj.toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'Fecha inválida';
        }
    };

    const limpiarFiltros = () => {
        setBusqueda('');
        setFiltroEstado('todos');
        setFiltroPagoCliente('todos');
        setFechaInicio(null);
        setFechaFin(null);
        
        // Si es trabajador, mantener el filtro de su propio ID
        if (userInfo?.rol !== 'trabajador') {
            setFiltroTrabajador('todos');
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <p>Cargando comisiones...</p>
                <button onClick={fetchData} className="btn-reintentar">
                    Reintentar
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>Error: {error}</p>
                <button onClick={fetchData} className="btn-reintentar">
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="comisiones-container">
            <h1>Gestión de Comisiones</h1>
            
            {userInfo && (
                <div className="user-info">
                    <p>Usuario: {userInfo.nombre} ({userInfo.rol})</p>
                    {userInfo.porcentaje_comision && (
                        <p>Porcentaje de comisión: {parseFloat(userInfo.porcentaje_comision).toFixed(2)}%</p>
                    )}
                </div>
            )}

            <div className="filtros-comisiones">
                <div className="filtro-group">
                    <label>Buscar:</label>
                    <input
                        type="text"
                        placeholder="Nombre del trabajador o cliente..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </div>
                
                <div className="filtro-group">
                    <label>Estado comisión:</label>
                    <select 
                        value={filtroEstado} 
                        onChange={(e) => setFiltroEstado(e.target.value)}
                    >
                        <option value="todos">Todos</option>
                        <option value="pendiente">Pendientes</option>
                        <option value="pagado">Pagadas</option>
                        <option value="rechazado">Rechazadas</option>
                    </select>
                </div>
                
                <div className="filtro-group">
                    <label>Trabajador:</label>
                    <select 
                        value={filtroTrabajador} 
                        onChange={(e) => setFiltroTrabajador(e.target.value)}
                        disabled={userInfo?.rol === 'trabajador'} // Deshabilitado para trabajadores
                    >
                        <option value="todos">Todos</option>
                        {trabajadores.map(t => (
                            <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                    </select>
                </div>
                
                <div className="filtro-group">
                    <label>Pago cliente:</label>
                    <select 
                        value={filtroPagoCliente} 
                        onChange={(e) => setFiltroPagoCliente(e.target.value)}
                    >
                        <option value="todos">Todos</option>
                        <option value="Sí">Pagado</option>
                        <option value="No">No pagado</option>
                    </select>
                </div>
                
                <div className="filtro-group rango-fechas">
                    <label>Desde:</label>
                    <DatePicker
                        selected={fechaInicio}
                        onChange={date => setFechaInicio(date)}
                        selectsStart
                        startDate={fechaInicio}
                        endDate={fechaFin}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fecha inicio"
                    />
                    
                    <label>Hasta:</label>
                    <DatePicker
                        selected={fechaFin}
                        onChange={date => setFechaFin(date)}
                        selectsEnd
                        startDate={fechaInicio}
                        endDate={fechaFin}
                        minDate={fechaInicio}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fecha fin"
                    />
                </div>
                
                <button onClick={limpiarFiltros} className="btn-limpiar">
                    Limpiar filtros
                </button>
                
                <button onClick={fetchData} className="btn-refresh">
                    Actualizar datos
                </button>
            </div>

            <div className="resumen-comisiones">
                <div className="resumen-card">
                    <h3>Total Pendiente</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .filter(c => c.estado === 'pendiente')
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
                
                <div className="resumen-card">
                    <h3>Total Pagado</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .filter(c => c.estado === 'pagado')
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
                
                <div className="resumen-card">
                    <h3>Total Comisiones</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
            </div>

            <div className="comisiones-list">
                {comisionesFiltradas.length === 0 ? (
                    <div className="no-comisiones">
                        No hay comisiones registradas con los filtros actuales
                    </div>
                ) : (
                    <table className="comisiones-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Trabajador</th>
                                <th>Remisión</th>
                                <th>Cliente</th>
                                <th>Monto Pedido</th>
                                <th>% Comisión</th>
                                <th>Total Comisión</th>
                                <th>Estado Pedido</th>
                                <th>Pago Cliente</th>
                                <th>Fecha</th>
                                <th>Estado Comisión</th>
                                {userInfo?.rol !== 'trabajador' && <th>Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {comisionesFiltradas.map(comision => (
                                <tr key={comision.id}>
                                    <td>{comision.id}</td>
                                    <td>{comision.trabajador_nombre}</td>
                                    <td>{comision.numeroremision}</td>
                                    <td>{comision.cliente_pedido}</td>
                                    <td>{formatPrice(comision.monto)}</td>
                                    <td>{parseFloat(comision.porcentaje).toFixed(2)}%</td>
                                    <td>{formatPrice(comision.valor_comision || calcularTotalComision(comision.monto, comision.porcentaje))}</td>
                                    <td className={`estado-pedido ${comision.estado_pedido?.toLowerCase().replace(' ', '-')}`}>
                                        {comision.estado_pedido}
                                    </td>
                                    <td className={`pago-cliente ${comision.cliente_pago === 'Sí' ? 'pagado' : 'no-pagado'}`}>
                                        {comision.cliente_pago === 'Sí' ? 'Pagado' : 'No pagado'}
                                    </td>
                                    <td>{formatFecha(comision.fecha)}</td>
                                    <td className={`estado-comision ${comision.estado}`}>
                                        {comision.estado}
                                    </td>
                                    {userInfo?.rol !== 'trabajador' && (
                                        <td className="acciones">
                                            {comision.estado === 'pendiente' && (
                                                <button 
                                                    onClick={() => updateEstado(comision.id, 'pagado')}
                                                    className="btn-pagar"
                                                    disabled={comision.cliente_pago !== 'Sí'}
                                                    title={comision.cliente_pago !== 'Sí' ? 'No se puede pagar hasta que el cliente haya pagado' : ''}
                                                >
                                                    Pagar
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Comisiones;